import { useQuery } from '@tanstack/react-query';
import moment from 'moment';

import type { TimestampInMillis, UUID } from '@cobliteam/common-types';

import {
  DriverScoreRequester,
  type FleetScore,
  GeocodeRequester,
  RiskEventsRequester,
  type RiskEventWithAddress,
} from '@cobliteam/api-lib';

import { Alert } from '@cobliteam/alert';
import { Environment } from '@cobliteam/react-environments';

const env = Environment.getInstance().getEnvironment();
const driverScoreRequester = new DriverScoreRequester({
  host: env.herbieApiHost,
});
const riskEventRequester = new RiskEventsRequester({ host: env.herbieApiHost });
const geocodeRequester = new GeocodeRequester({ host: env.herbieApiHost });

const FLEET_SCORE_KEY = 'FLEET-SCORE';
const RISK_EVENT_KEY = 'RISK-EVENT-WITH-ADDRESS';

const handleRiskEventsError = () => {
  Alert.error(
    'Houve um erro ao carregar os eventos de risco, tente novamente mais tarde.'
  );
};

const handleFleetScoreError = () => {
  Alert.error('Houve um erro, tente novamente mais tarde.');
};

const useDriverBehavior = () => {
  const getFleetScore = () => {
    return useQuery<FleetScore>({
      queryKey: [FLEET_SCORE_KEY],
      queryFn: () => driverScoreRequester.getFleetScore(),
      onError: handleFleetScoreError,
    });
  };

  const getRiskEvents = (selectedDate: TimestampInMillis, driverId: UUID) => {
    return useQuery<RiskEventWithAddress[]>({
      queryKey: [RISK_EVENT_KEY],
      queryFn: async () => {
        const period = {
          start: moment(selectedDate).startOf('day').valueOf(),
          end: moment(selectedDate).endOf('day').valueOf(),
        };

        const riskEvents = await riskEventRequester.getRiskEvents(
          period,
          driverId
        );

        const latLongs = riskEvents.map((riskEvent) => ({
          lat: riskEvent.latitude,
          lng: riskEvent.longitude,
        }));

        const addresses = await geocodeRequester.getAddresses(latLongs);

        return riskEvents.map((riskEvent, index) => ({
          address: addresses[index],
          eventTimestamp: riskEvent.eventTimestamp,
          eventType: riskEvent.eventType,
        }));
      },
      onError: handleRiskEventsError,
    });
  };

  return {
    getFleetScore,
    getRiskEvents,
  };
};

export default useDriverBehavior;
